import React, { useEffect, useState } from "react";
import { X, Phone } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import ApiService from "../../services/Api.service";

const AddCallNumberModel = ({ isOpen, onClose, callNumberToEdit, onSave }) => {
  const [ivrDropdowns, setIvrDropdowns] = useState({
    dialer_options: [],
  });
  const [languageOptions, setLanguageOptions] = useState({})
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const { handleSubmit, control, reset, watch, formState: { errors } } = useForm({
  defaultValues: {
    number: "",
    concurrent_limit: 1,
    dialer: "",
    api_key: "",
    api_token: "",
    app_id: "",
    exotel_endpoint: "",
  },
});

 // Fetch dropdown data from backend
    const fetchLanguageOptions = async () => {
        try {
            const languageRes = await ApiService.getLanguageOptions();
            if (languageRes.status === 200) {
                setLanguageOptions(languageRes.data);

                const defaultLang =
                    Array.isArray(languageRes.data?.default_language) && languageRes.data.default_language.length > 0
                        ? languageRes.data.default_language[0].value
                        : "";
                setSelectedLanguage(defaultLang);  // This will trigger your useEffect that fetches dropdown
            }
        } catch (error) {
            toast.error("Failed to load IVR configuration options");
        }
    };


    useEffect(() => {
        fetchLanguageOptions()
    }, []);

  // Fetch dropdowns for Dialer list
  const fetchDropdownDetail = async () => {
    try {
      const payload = {
                language: selectedLanguage
            }
      const ivrOptionsRes = await ApiService.getIvrOptions(payload);
      if (ivrOptionsRes.status === 200) {
        setIvrDropdowns({
          dialer_options: ivrOptionsRes.data?.dialer_options || [],
        });
      }
    } catch (error) {
      console.error("Error fetching IVR options:", error);
      toast.error("Failed to load dialer options");
    }
  };

  useEffect(() => {
    fetchDropdownDetail();
  }, []);

  // Reset form when editing
  useEffect(() => {
    if (callNumberToEdit) {
      reset({
        number: callNumberToEdit.number || "",
        concurrent_limit: callNumberToEdit.concurrent_limit || 1,
        dialer: callNumberToEdit.dialer || "",
        api_key: callNumberToEdit.api_key || "",
        api_token: callNumberToEdit.api_token || "",
        app_id: callNumberToEdit.app_id || "",
        exotel_endpoint: callNumberToEdit.exotel_endpoint || "",
      });
    } else {
      reset();
    }
  }, [callNumberToEdit, isOpen, reset]);

  const onSubmit = (data) => {
    const payload = {
      number: data.number.trim(),
      concurrent_limit: Number(data.concurrent_limit),
      dialer: data.dialer,
      api_key: data.api_key.trim(),
      api_token: data.api_token.trim(),
      app_id: data.app_id.trim(),
      exotel_endpoint: data.exotel_endpoint.trim(),
    };

    onSave(payload);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="add-subparam-overlay">
      <div className="add-subparam-modal">
        {/* Header */}
        <div className="add-subparam-header">
          <h3>
            <Phone size={18} style={{ marginRight: "6px" }} />
            {callNumberToEdit ? "Edit Call Number" : "Add Call Number"}
          </h3>
          <button onClick={onClose} className="close-btn">
            <X size={20} />
          </button>
        </div>

        {/* Body Form */}
        <form id="callNumberForm" onSubmit={handleSubmit(onSubmit)} className="add-subparam-body">
          <label>Phone Number</label>
          <Controller
            name="number"
            control={control}
            rules={{ required: "Phone number is required" }}
            render={({ field }) => (
              <input
                {...field}
                className={`form-control ${errors.number ? "error" : ""}`}
                placeholder="Enter phone number"
              />
            )}
          />
          {errors.number && <p className="error-text">{errors.number.message}</p>}

          <label>Concurrent Limit</label>
          <Controller
            name="concurrent_limit"
            control={control}
            rules={{ required: "Concurrent limit is required", min: 1 }}
            render={({ field }) => (
              <input
                type="number"
                {...field}
                className={`form-control ${errors.concurrent_limit ? "error" : ""}`}
                min="1"
                placeholder="Enter concurrent call limit"
              />
            )}
          />
          {errors.concurrent_limit && <p className="error-text">{errors.concurrent_limit.message}</p>}

          <label>Dialer Type</label>
          <Controller
            name="dialer"
            control={control}
            rules={{ required: "Dialer is required" }}
            render={({ field }) => (
              <select {...field} className={`form-control ${errors.dialer ? "error" : ""}`}>
                <option value="">Select Dialer</option>
                {ivrDropdowns.dialer_options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.dialer && <p className="error-text">{errors.dialer.message}</p>}

           <label>API Keys</label>
              <Controller
                name="api_key"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    className="form-control"
                    placeholder="Enter API Key"
                  />
                )}
              />


          {/* ✅ Exotel fields are shown only if dialer === 'exotel' */}
          {watch("dialer") === "Exotel" && (
            <>
             

              <label>API Token</label>
              <Controller
                name="api_token"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    className="form-control"
                    placeholder="Enter API Token"
                  />
                )}
              />

              <label>App ID</label>
              <Controller
                name="app_id"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    className="form-control"
                    placeholder="Enter App ID"
                  />
                )}
              />

              <label>Exotel Endpoint</label>
              <Controller
                name="exotel_endpoint"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    className="form-control"
                    placeholder="Enter Exotel endpoint"
                  />
                )}
              />
            </>
          )}
        </form>


        {/* Footer */}
        <div className="add-subparam-footer">
          <button type="button" className="popup-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="callNumberForm" className="popup-add-btn">
            {callNumberToEdit ? "Update Number" : "Save Number"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCallNumberModel;
